import { FormEvent, ReactNode, useEffect, useState } from 'react';

import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { TextArea } from '../../components/TextArea';
import { CloseIcon } from '../../icons/CloseIcon';
import { InfoIcon } from '../../icons/InfoIcon';
import {
  Body,
  DismissButton,
  FooterInner,
  InfoAlert,
  InfoAlertLink,
  InfoAlertText,
  InfoIconWrapper,
  InlineBody,
  InlineCloseButton,
  InlineFooter,
  InlineHeader,
  InlinePanel,
  InlineTitle,
  SuccessBody,
  SuccessMessage,
  SuccessTitle,
} from './BookingRequestModal.styles';
import type {
  BookingRequestModalLabels,
  BookingRequestModalProps,
} from './BookingRequestModal.types';

const DEFAULT_LABELS: Required<BookingRequestModalLabels> = {
  amendTitle: 'Request a change to your booking',
  cancelTitle: 'Request to cancel your booking',
  amendPlaceholder: "Tell us what you'd like to change*",
  cancelPlaceholder: "Tell us why you'd like to cancel*",
  submit: 'Submit request',
  close: 'Close',
  dismissAlert: 'Dismiss',
  cancelAlertBefore: 'To cancel specific items, use ',
  cancelAlertLink: 'amend',
  amendAlertBefore: 'To cancel your booking, use ',
  amendAlertLink: 'cancel',
  alertAfter: ' instead.',
  successTitle: 'Request sent',
  successMessage: 'Your request has been sent and you will be contacted shortly to confirm.',
  done: 'Done',
};

const FORM_ID = 'booking-request-form';

export function BookingRequestModal(props: BookingRequestModalProps) {
  const {
    open,
    variant,
    value,
    onChange,
    onSubmit,
    onSwitchToAmend,
    onSwitchToCancel,
    onDone,
    isSubmitting = false,
    isSuccess = false,
    showSwitchAlert = true,
    labels: labelOverrides,
    metadata,
  } = props;
  const onClose = props.onClose;
  const isInline = props.presentation === 'inline';

  const labels: Required<BookingRequestModalLabels> = { ...DEFAULT_LABELS, ...labelOverrides };
  const [alertDismissed, setAlertDismissed] = useState(false);

  useEffect(() => {
    if (!open) setAlertDismissed(false);
  }, [open]);

  useEffect(() => {
    setAlertDismissed(false);
  }, [variant]);

  const isCancel = variant === 'cancel';
  const title = isCancel ? labels.cancelTitle : labels.amendTitle;
  const placeholder = isCancel ? labels.cancelPlaceholder : labels.amendPlaceholder;
  const switchHandler = isCancel ? onSwitchToAmend : onSwitchToCancel;
  const shouldShowAlert = showSwitchAlert && !alertDismissed && Boolean(switchHandler);
  const submitDisabled = isSubmitting || value.trim().length === 0;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitDisabled) return;
    onSubmit();
  };

  const alertBefore = isCancel ? labels.cancelAlertBefore : labels.amendAlertBefore;
  const alertLink = isCancel ? labels.cancelAlertLink : labels.amendAlertLink;
  const { alertAfter } = labels;

  const alert: ReactNode = shouldShowAlert ? (
    <InfoAlert role="note">
      <InfoIconWrapper aria-hidden="true">
        <InfoIcon size={18} />
      </InfoIconWrapper>
      <InfoAlertText>
        {alertBefore}
        <InfoAlertLink type="button" onClick={switchHandler}>
          {alertLink}
        </InfoAlertLink>
        {alertAfter}
      </InfoAlertText>
      <DismissButton
        type="button"
        aria-label={labels.dismissAlert}
        onClick={() => setAlertDismissed(true)}
      >
        <CloseIcon size={12} />
      </DismissButton>
    </InfoAlert>
  ) : null;

  const handleDone = onDone ?? onClose;

  const successContent = (
    <SuccessMessage role="status">
      <SuccessTitle>{labels.successTitle}</SuccessTitle>
      <SuccessBody>{labels.successMessage}</SuccessBody>
    </SuccessMessage>
  );

  const formContent = (
    <>
      {metadata}
      {alert}
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <TextArea
          data-testid="booking-request-text-area"
          aria-label={placeholder}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isSubmitting}
          required
          rows={5}
        />
      </form>
    </>
  );

  const bodyContent = isSuccess ? successContent : formContent;

  const footerButton = isSuccess ? (
    <Button type="button" variant="primary" fullWidth onClick={handleDone} disabled={!handleDone}>
      {labels.done}
    </Button>
  ) : (
    <Button
      type="submit"
      form={FORM_ID}
      variant={isCancel ? 'danger' : 'primary'}
      fullWidth
      loading={isSubmitting}
      disabled={submitDisabled}
    >
      {labels.submit}
    </Button>
  );

  if (isInline) {
    if (!open) return null;
    return (
      <InlinePanel role="region" aria-label={title}>
        <InlineHeader>
          <InlineTitle>{title}</InlineTitle>
          {onClose && (
            <InlineCloseButton type="button" aria-label={labels.close} onClick={onClose}>
              &#x2715;
            </InlineCloseButton>
          )}
        </InlineHeader>
        <InlineBody>
          <Body>{bodyContent}</Body>
        </InlineBody>
        <InlineFooter>
          <FooterInner>{footerButton}</FooterInner>
        </InlineFooter>
      </InlinePanel>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose as () => void}
      title={title}
      ariaLabel={title}
      labels={{ close: labels.close }}
      footer={<FooterInner>{footerButton}</FooterInner>}
    >
      <Body>{bodyContent}</Body>
    </Modal>
  );
}
